import { useState, useCallback, useEffect } from 'react';

interface GeolocationState {
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  error: GeolocationPositionError | null;
  loading: boolean;
}

interface UseGeolocationReturn extends GeolocationState {
  refresh: () => void;
}

interface UseGeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
}

export function useGeolocation(options: UseGeolocationOptions = {}): UseGeolocationReturn {
  const { enableHighAccuracy = true, timeout = 10000 } = options;

  const [state, setState] = useState<GeolocationState>({
    latitude: null,
    longitude: null,
    accuracy: null,
    error: null,
    loading: false,
  });

  const onSuccess = useCallback((position: GeolocationPosition) => {
    setState({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      error: null,
      loading: false,
    });
  }, []);

  const onError = useCallback((error: GeolocationPositionError) => {
    setState((prev) => ({ ...prev, error, loading: false }));
  }, []);

  const refresh = useCallback(() => {
    if (!navigator.geolocation) return;
    setState((prev) => ({ ...prev, loading: true }));
    navigator.geolocation.getCurrentPosition(onSuccess, onError, {
      enableHighAccuracy,
      timeout,
      maximumAge: 0,
    });
  }, [enableHighAccuracy, timeout, onSuccess, onError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { ...state, refresh };
}
